"use client";

import { PORTALS } from ".constants/portals";
import undressConfig from ".constants/undress.config.json";
import {
  PortalModal,
  ModalHeader,
  ModalContent,
  Icon,
  usePortalContext,
  Button,
  ModalFooter,
  Textarea,
} from "@cloai/uikit";
import { useTranslations } from "next-intl";
import { ChangeEvent, memo, useCallback } from "react";

import { useUndressContext } from "@/providers";

import { STYLES } from "./vars";

export const PromptPortal = memo(() => {
  const t = useTranslations("HomePage");
  const {
    loading,
    clickFileId,
    clickSettings,
    clickUndressingFile,
    initialSettings,
    onReUseFaceObj,
    reUseFaceObj,
    updatePhotoSetting,
  } = useUndressContext();
  const { closePortal, openPortal } = usePortalContext();

  const { pose } = clickSettings;

  const promptValue = clickSettings.prompt;

  const onWrite = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      updatePhotoSetting(clickFileId, "prompt", event.target.value);
    },
    [clickFileId, updatePhotoSetting],
  );

  const onReset = useCallback(() => {
    updatePhotoSetting(clickFileId, "prompt", initialSettings.prompt);
  }, [clickFileId, initialSettings.prompt, updatePhotoSetting]);

  const onNext = useCallback(() => {
    closePortal();

    if (pose === initialSettings.pose) {
      return openPortal(PORTALS.USER_FLOW_POSES);
    }
    if (reUseFaceObj) {
      return onReUseFaceObj();
    }

    if (!clickUndressingFile) {
      return openPortal(PORTALS.USER_FLOW_UPLOAD);
    }
  }, [
    clickUndressingFile,
    closePortal,
    initialSettings.pose,
    onReUseFaceObj,
    openPortal,
    pose,
    reUseFaceObj,
  ]);

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_PROMPT}
      repositionInputs={false}
      style={STYLES.portal.variants.medium}
    >
      <ModalHeader>WRITE YOUR PROMPT</ModalHeader>

      <ModalContent className="@container">
        <Textarea
          placeholder={t("promtTextareaPlaceholder")}
          value={promptValue}
          onChange={onWrite}
          onReset={onReset}
          maxLength={undressConfig.UndressModifications.maxPromptLength}
          // disabled={!isOpen || !isPromtSelected}
          className="!min-h-50"
        />
      </ModalContent>
      <ModalFooter>
        <Button
          startContent={<Icon icon="double_arrow" />}
          color="primary"
          wide
          centerText
          onClick={onNext}
          loading={loading}
          disabled={!promptValue}
        >
          {t("next")}
        </Button>
      </ModalFooter>
    </PortalModal>
  );
});

PromptPortal.displayName = "PromptPortal";
